import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthContext } from "./context/AuthContext";
import { useContext, useEffect } from "react";
import Login from "./components/Login";
import PrivateRoute from "./components/PrivateRoutes";
import SuperAdminHome from "./components/pages/Super Admin/SuperAdminHome";
import InstituteStats from "./components/pages/Super Admin/InstituteStats";
import UsageActivity from "./components/pages/Super Admin/pageUI/UsageActivity";
import AdminHome from "./components/pages/Admin/AdminHome";
import InstructorHome from "./components/pages/Instructor/InstructorHome";
import StudentHome from "./components/pages/Student/StudentHome";
import ViewClassHome from "./components/pages/Student/pageUI/ViewClassHome";
import StudentViewResults from "./components/pages/Student/pageUI/results/StudentViewResults";
import StudentExamMode from "./components/pages/Student/pageUI/StudentExamMode";
import ExamInstructions from "./components/pages/Student/pageUI/ExamInstructions";
import ManageAdmins from "./components/pages/Super Admin/pageUI/ManageAdmins";
import ManageClassHome from "./components/pages/Instructor/pageUI/ManageClassHome";
import TestGradingHome from "./components/pages/Instructor/pageUI/testGrading/TestGradingHome";
import ViewResults from "./components/pages/Instructor/pageUI/Results/ViewResults";
import ViewByQuestion from "./components/pages/Instructor/pageUI/Results/ViewByQuestion";
import ScoreHistory from "./components/pages/Instructor/pageUI/ScoreHistory/ScoreHistory";
import ResetPassword from "./components/ResetPassword";
import ManageTokenUsage from "./components/pages/Super Admin/pageUI/ManageTokenUsage";
import VerifyAccount from "./components/VerifyAccount";
import SuccessPage from "./components/pages/QuickRegister/Common/SuccessPage";
import ManageModelConfig from "./components/pages/Instructor/pageUI/testGrading/ManageModelConfig";
import ManageContract from "./components/pages/Super Admin/pageUI/ManageContract";
import ManageTerms from "./components/pages/Super Admin/pageUI/ManageTerms";
import GetAllAnswerPapers from "./components/pages/Super Admin/pageUI/GetAllAnswerPapers";
import GetAllBatchJobs from "./components/pages/Super Admin/pageUI/GetAllBatchJobs";
import ManageAIConfig from "./components/pages/Super Admin/pageUI/ManageAIConfig";
import Resources from "./components/pages/Super Admin/pageUI/Resources";
import WhatsAppNotifications from "./components/pages/Super Admin/pageUI/WhatsAppNotifications";
import ManageLOVs from "./components/pages/Super Admin/pageUI/ManageLOVs";
import ManageFAQ from "./components/pages/Super Admin/pageUI/ManageFAQ";
import TermsOfServices from "./components/pages/QuickRegister/Common/TermsOfServices";
import PrivacyPolicy from "./components/pages/QuickRegister/Common/PrivacyPolicy";
import UserProfile from "./components/ui/UserProfile";
import AdminCreditUsage from "./components/pages/Admin/AdminCreditUsage";
import AdminWhatsAppNotifications from "./components/pages/Admin/AdminWhatsAppNotifications";
import TourPage from "./components/ui/GuidedTour/TourPage";
import DemoResultPage from "./components/ui/GuidedTour/DemoResultPage";
import PriceCalculator from "./components/pages/PriceCalculator/PriceCalculator";
import InvoiceGenerator from "./components/ui/InvoiceGenerator/InvoiceGenerator";
// import QuickRegister from "./components/pages/QuickRegister/QuickRegister";
// import CredentialsForm from "./components/pages/QuickRegister/Common/CredentialsForm";
import UPSCUserHome from "./components/pages/UPSCUser/UPSCUserHome";
import UPSCResults from "./components/pages/UPSCUser/UPSCResults";
import IndividualHome from "./components/pages/Individual/IndividualHome";
import IndividualResults from "./components/pages/Individual/IndividualResults";
import ManageInstitutes from "./components/pages/Super Admin/pageUI/ManageInstitutes";
import ManageOMRTemplates from "./components/pages/Super Admin/pageUI/ManageOMRTemplates";
import OMRStats from "./components/pages/Super Admin/pageUI/OMRStats";
import ManageLibrary from "./components/pages/Super Admin/pageUI/ManageLibrary";
import ManageStandardizedTests from "./components/pages/Super Admin/pageUI/ManageStandardizedTests";
import LibraryStudentStats from "./components/pages/Super Admin/pageUI/LibraryStudentStats";
import ManageCertificates from "./components/pages/Super Admin/pageUI/ManageCertificates";
import VerifyCertificate from "./components/ui/Certificate/VerifyCertificate";
import ManageMaintenanceMode from "./components/pages/Super Admin/pageUI/ManageMaintenanceMode";
import MaintenancePage from "./components/ui/MaintenancePage";
import { useMaintenance } from "./context/MaintenanceContext";
import { usePushAutoSubscribe } from "./hooks/usePushAutoSubscribe";

function App() {
  const { isAuthenticated, primaryRole, user } = useContext(AuthContext);

  // Auto-register + prompt for push once the user is authenticated
  usePushAutoSubscribe();

  useEffect(() => {
    const handleGlobalWheel = (e) => {
      // Check if the scroll target is a number input and it's the active element
      if (e.target.type === "number" && e.target === document.activeElement) {
        e.target.blur();
      }
    };

    // { passive: false } is critical to allow e.preventDefault()
    document.addEventListener("wheel", handleGlobalWheel, { passive: false });

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener("wheel", handleGlobalWheel);
    };
  }, []);

  const { isUnderMaintenance, isSuperAdmin } = useMaintenance();

  // If system is currently in active maintenance, smoothly show MaintenancePage to non-superadmin users
  const isSuperAdminAccess =
    isSuperAdmin && window.location.pathname.startsWith("/superadmin");
  const isLoginPage = window.location.pathname === "/login";
  if (isUnderMaintenance && !isSuperAdminAccess && !isLoginPage) {
    return (
      <Router>
        <MaintenancePage />
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Default route - redirect based on auth status and primary role */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              user?.category === "INDIVIDUAL" ? (
                <Navigate to="/individual" replace />
              ) : user?.category === "UPSC" ? (
                <Navigate to="/upsc" replace />
              ) : (
                <Navigate to={`/${primaryRole}`} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Public Route - Redirect authenticated users from login page */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              user?.category === "INDIVIDUAL" ? (
                <Navigate to="/individual" replace />
              ) : user?.category === "UPSC" ? (
                <Navigate to="/upsc" replace />
              ) : (
                <Navigate to={`/${primaryRole}`} replace />
              )
            ) : (
              <Login />
            )
          }
        />
        {/* Public Route - Redirect users from Quick Register page */}
        {/* <Route path="/quick-register/*" element={<QuickRegister />} /> */}

        {/* Temporary Disable the registration form for individual and upsc */}
        {/* <Route path="/quick-register" element={<CredentialsForm />} /> */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-account" element={<VerifyAccount />} />
        <Route path="/success/:email" element={<SuccessPage />} />
        <Route path="/tos" element={<TermsOfServices />} />
        <Route path="/pp" element={<PrivacyPolicy />} />
        <Route path="/user-profile" element={<UserProfile />} />
        <Route
          path="/certificate/verify/:code"
          element={<VerifyCertificate />}
        />

        {/* Protected routes with role checks */}
        <Route
          path="/superadmin"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <SuperAdminHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/manage-admins/:id"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageAdmins />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/manage-token-usage/:id"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageTokenUsage />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/manage-contract/:id"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageContract />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/manage-institutes"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageInstitutes />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/manage-terms"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageTerms />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/total-token-usage"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <UsageActivity />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/stats"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <InstituteStats />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/answer-papers/:id"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <GetAllAnswerPapers />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/batch-jobs/:id"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <GetAllBatchJobs />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/ai-config"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageAIConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/invoice-generator"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <InvoiceGenerator />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/resources"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <Resources />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/library"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageLibrary />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/whatsapp-notifications"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <WhatsAppNotifications />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/lov-management"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageLOVs />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/faq"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageFAQ />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/omr-templates"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageOMRTemplates />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/omr-stats"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <OMRStats />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/standardized-tests"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageStandardizedTests />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/library-stats"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <LibraryStudentStats />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/manage-certificates"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageCertificates />
            </PrivateRoute>
          }
        />
        <Route
          path="/superadmin/manage-maintenance"
          element={
            <PrivateRoute roles={["superadmin"]}>
              <ManageMaintenanceMode />
            </PrivateRoute>
          }
        />
        <Route
          path="/maintenance"
          element={<MaintenancePage />}
        />

        <Route
          path="/admin/*"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminHome />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/manage-institute/grading-credit-usage/*"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminCreditUsage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/whatsapp-notifications"
          element={
            <PrivateRoute roles={["admin"]}>
              <AdminWhatsAppNotifications />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/results/:answerpaperId/*"
          element={
            <PrivateRoute roles={["admin"]}>
              <ViewResults />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/score-by-question/:testId"
          element={
            <PrivateRoute roles={["admin"]}>
              <ViewByQuestion />
            </PrivateRoute>
          }
        />

        <Route
          path="/instructor/*"
          element={
            <PrivateRoute roles={["instructor"]}>
              <InstructorHome />
            </PrivateRoute>
          }
        />

        <Route
          path="/instructor/manage-class/:classId/*"
          element={
            <PrivateRoute roles={["instructor"]}>
              <ManageClassHome />
            </PrivateRoute>
          }
        />

        <Route
          path="/instructor/test-grading/:testId/*"
          element={
            <PrivateRoute roles={["instructor"]}>
              <TestGradingHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/instructor/manage-model-config/*"
          element={
            <PrivateRoute roles={["instructor"]}>
              <ManageModelConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="/instructor/results/:answerpaperId/*"
          element={
            <PrivateRoute roles={["instructor"]}>
              <ViewResults />
            </PrivateRoute>
          }
        />

        <Route
          path="/instructor/score-by-question/:testId"
          element={
            <PrivateRoute roles={["instructor"]}>
              <ViewByQuestion />
            </PrivateRoute>
          }
        />

        <Route
          path="/instructor/score-history/:answerpaperId/*"
          element={
            <PrivateRoute roles={["instructor"]}>
              <ScoreHistory />
            </PrivateRoute>
          }
        />

        <Route
          path="/instructor/tour"
          element={
            <PrivateRoute roles={["instructor"]}>
              <TourPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/instructor/tour/results"
          element={
            <PrivateRoute roles={["instructor"]}>
              <DemoResultPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/student/*"
          element={
            <PrivateRoute roles={["student"]}>
              <StudentHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/student/view-class/:classId/*"
          element={
            <PrivateRoute roles={["student"]}>
              <ViewClassHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/student/results/:answerpaperId/*"
          element={
            <PrivateRoute roles={["student"]}>
              <StudentViewResults />
            </PrivateRoute>
          }
        />
        <Route
          path="/student/exam-instructions/:testId"
          element={
            <PrivateRoute roles={["student"]}>
              <ExamInstructions />
            </PrivateRoute>
          }
        />
        <Route
          path="/student/exam/:testId"
          element={
            <PrivateRoute roles={["student"]}>
              <StudentExamMode />
            </PrivateRoute>
          }
        />

        {/* Individual Routes */}
        <Route
          path="/individual"
          element={
            <PrivateRoute roles={["instructor"]}>
              <IndividualHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/individual/results/:answerpaperId"
          element={
            <PrivateRoute roles={["instructor"]}>
              <IndividualResults />
            </PrivateRoute>
          }
        />
        {/* UPSC User Routes */}
        <Route
          path="/upsc"
          element={
            <PrivateRoute roles={["instructor"]}>
              <UPSCUserHome />
            </PrivateRoute>
          }
        />
        <Route
          path="/upsc/results/:answerpaperId"
          element={
            <PrivateRoute roles={["instructor"]}>
              <UPSCResults />
            </PrivateRoute>
          }
        />

        {/* Price Calculator — Hidden, admin-only, noindex */}
        <Route
          path="/admin/price-calculator"
          element={
            <PrivateRoute roles={["admin"]}>
              <PriceCalculator />
            </PrivateRoute>
          }
        />

        {/* Unauthorized access route */}
        <Route
          path="/unauthorized"
          element={
            isAuthenticated ? (
              user?.category === "INDIVIDUAL" ? (
                <Navigate to="/individual" replace />
              ) : user?.category === "UPSC" ? (
                <Navigate to="/upsc" replace />
              ) : (
                <Navigate to={`/${primaryRole}`} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Catch all route */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              user?.category === "INDIVIDUAL" ? (
                <Navigate to="/individual" replace />
              ) : user?.category === "UPSC" ? (
                <Navigate to="/upsc" replace />
              ) : (
                <Navigate to={`/${primaryRole}`} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
